import { useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";

import type {
  Patient,
  PatientIndexSearchResultV2,
  PatientSearchRequestV2,
  SaveSearchReasoningV2Request,
  SaveSearchReasoningV2Response,
  SuccessPatientIndexSearchResultV2,
  TooManyMatchesPatientIndexSearchResultV2,
  UUID,
} from "@thalamos/aspire-common";

import { CollectCriteria } from "./CollectCriteria.js";
import type {
  CollectCriteriaDemographicsProps,
  ValidationSchema,
} from "./CollectCriteriaDemographics.js";
import type { CollectCriteriaNhsNumberProps } from "./CollectCriteriaNhsNumber.js";
import type { CollectCriteriaPdsDemographicsProps } from "./CollectCriteriaPdsDemographics.js";
import { SearchResults } from "./SearchResults.js";
import { ageRangeToDateRange } from "./util.js";
import { api } from "~/api.js";
import {
  Banner,
  BannerList,
  Button,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
} from "~/components/design-system/index.js";
import { FormFooterSection } from "~/components/form/FormFooter.js";
import { HorizontalLine } from "~/components/form/HorizontalLine.js";
import type { SelectedTrust } from "~/components/form/TrustPicker.js";
import { TrustPicker } from "~/components/form/TrustPicker.js";
import { PatientBanner } from "~/components/layout/PatientBanner/PatientBanner.js";

/**
 * The criteria types available for reconciliation search
 */
export type ReconciliationCriteriaType =
  "nhs-number" | "demographics" | "pds-demographics";

/**
 * Configuration for which criteria types are allowed
 */
export type AllowedCriteriaTypes = {
  /** Allow PDS NHS number search (default: true) */
  pdsNhsNumber?: boolean;
  /** Allow local demographics search (default: false) */
  demographics?: boolean;
  /** Allow PDS demographics search (default: true) */
  pdsDemographics?: boolean;
};

/**
 * API functions that can be injected for testing/storybook
 */
export type PatientReconciliationSearchApiProps = {
  searchV2: (
    searchId: string,
    query: PatientSearchRequestV2,
  ) => Promise<{ data: PatientIndexSearchResultV2 }>;
  addSearchV2Reasoning: (
    resultId: string,
    body: SaveSearchReasoningV2Request,
  ) => Promise<{ status: number; data: SaveSearchReasoningV2Response }>;
  mergePatients: (params: {
    currentPatientId: string;
    targetPatientId: string;
    reason: string;
    isAutoMerge: boolean;
  }) => Promise<{ status: number; data: { reason?: string } }>;
};

/**
 * Default API implementation using the real API
 */
export const defaultReconciliationApi: PatientReconciliationSearchApiProps = {
  searchV2: async (searchId, query) => {
    const result = await api.patients.searchV2(searchId, query);
    return { data: result.data };
  },
  addSearchV2Reasoning: async (resultId, body) => {
    const result = await api.patients.addSearchV2Reasoning(resultId, body);
    return { status: result.status, data: result.data };
  },
  mergePatients: async (params) => {
    const result = await api.patients.merge(params);
    return {
      status: result.status,
      data: result.status === 204 ? {} : (result.data as { reason?: string }),
    };
  },
};

export type PatientReconciliationSearchModalProps = {
  open: boolean;
  onClose: () => void;
  onComplete: (result: { type: "patient-merged"; patientId: UUID }) => void;
  currentPatientId: UUID;
  /**
   * The form context this reconciliation belongs to. Required for the
   * Vision/S136 path; omitted for patient-context reconciliation (gated by
   * the Aspire `reconciliationViaNhsNumberEnabled` flag).
   */
  formContextId?: UUID;
  /**
   * The PDS organisation to search within. If undefined, the user will be
   * prompted to select a trust before proceeding with the search.
   */
  pdsOrg?: {
    odsCode: string;
  };
  /**
   * Configuration for which criteria types are allowed.
   * Default: { pdsNhsNumber: true, demographics: false, pdsDemographics: true }
   */
  allowedCriteriaTypes?: AllowedCriteriaTypes;
  /**
   * Optional API injection for testing/storybook
   */
  apiOverrides?: PatientReconciliationSearchApiProps;
  /**
   * The current patient record to display in the banner and pre-fill demographics.
   * When provided, a PatientBanner is shown at the top of the dialog
   * and demographic search fields are pre-filled.
   */
  currentPatient?: Patient;
  /**
   * The NHS number of the current patient (if known).
   */
  currentPatientNhsNumber?: string;
};

/**
 * Contains a set of search results, and the handlers to act upon them.
 */
type SearchResultsAndHandlers = {
  searchResults:
    | "loading"
    | TooManyMatchesPatientIndexSearchResultV2
    | SuccessPatientIndexSearchResultV2;

  /**
   * The function we will call if we want to continue with a search result.
   * @param index The index of the selected search result.
   */
  onContinue?: (index: number) => Promise<void>;
};

function PdsSearchFailedPopup({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  return (
    <PopupDialog
      open={open}
      onClose={() => {
        setOpen(false);
      }}
    >
      <PopupDialogTitle
        titleText={`PDS Search Failed`}
        closeDialog={() => {
          setOpen(false);
        }}
      />
      <Typography>
        Search of PDS failed - only local results shown. Please try again later.
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: "1em",
        }}
      >
        <Button
          variant="outlined"
          label={"Close"}
          onClick={() => {
            setOpen(false);
          }}
        />
      </Box>
    </PopupDialog>
  );
}

function SupersededPatientPopup({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  return (
    <PopupDialog
      open={open}
      onClose={() => {
        setOpen(false);
      }}
    >
      <PopupDialogTitle
        titleText={`NHS number has been updated`}
        closeDialog={() => {
          setOpen(false);
        }}
      />
      <Typography>
        The NHS number you searched for has been replaced (aka superseded) in
        NHS national systems - the patient should be informed of this if they
        have not been already.
      </Typography>
      <Banner
        sx={{ mt: "1em" }}
        bannerType={BannerList.WARNING}
        title="Results may be unexpected - please review carefully before proceeding."
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: "1em",
        }}
      >
        <Button
          variant="outlined"
          label={"Close"}
          onClick={() => {
            setOpen(false);
          }}
        />
      </Box>
    </PopupDialog>
  );
}

export const PatientReconciliationSearchModal = ({
  open,
  onClose,
  onComplete,
  currentPatientId,
  formContextId,
  pdsOrg,
  allowedCriteriaTypes,
  apiOverrides,
  currentPatient,
  currentPatientNhsNumber,
}: PatientReconciliationSearchModalProps) => {
  // Resolve allowed criteria types with defaults
  const canSearchByPdsNhsNumber = allowedCriteriaTypes?.pdsNhsNumber ?? true;
  const canSearchByDemographics = allowedCriteriaTypes?.demographics ?? false;
  const canSearchByPdsDemographics =
    allowedCriteriaTypes?.pdsDemographics ?? true;

  // Validate that at least one criteria type is enabled
  if (
    !canSearchByPdsNhsNumber &&
    !canSearchByDemographics &&
    !canSearchByPdsDemographics
  ) {
    throw new Error(
      "PatientReconciliationSearchModal: At least one criteria type must be enabled",
    );
  }

  // Determine the default criteria type - prefer NHS number, then PDS demographics, then local
  const getDefaultCriteriaType = (): ReconciliationCriteriaType => {
    if (canSearchByPdsNhsNumber) return "nhs-number";
    if (canSearchByPdsDemographics) return "pds-demographics";
    if (canSearchByDemographics) return "demographics";
    // This should never happen due to the validation above
    throw new Error("No criteria type available");
  };

  const theme = useTheme();
  const { t } = useTranslation();

  // Use injected API or default
  const apiClient = apiOverrides ?? defaultReconciliationApi;

  // This is where we store the search result from the API
  const [searchResultsAndHandlers, setSearchResultsAndHandlers] = useState<
    SearchResultsAndHandlers | undefined
  >(undefined);

  // Build initial demographics from the current patient for pre-filling search fields
  const initialDemographics = useMemo<ValidationSchema | undefined>(() => {
    if (!currentPatient) return undefined;

    const normalizeUnknown = (value?: string | null) => {
      if (!value) return "";
      return value.trim().toLowerCase() === "unknown" ? "" : value;
    };

    const givenName = normalizeUnknown(currentPatient.name.given);
    const familyName = normalizeUnknown(currentPatient.name.family);
    const dateOfBirthExact = normalizeUnknown(currentPatient.dateOfBirth);

    return {
      givenName,
      familyName,
      dateOfBirthIsExact: dateOfBirthExact ? true : null,
      dateOfBirthExact,
      usePhoneticSurnameMatch: false,
      usePhoneticGivenNameMatch: false,
      ageApprox: "",
    };
  }, [currentPatient]);

  // This is where we store the last searched demographics
  const [searchedDemographics, setSearchedDemographics] = useState<
    ValidationSchema | undefined
  >(initialDemographics);

  // This is where we store the index of the selected search
  const [searchResultIndexSelected, setSearchResultIndexSelected] = useState<
    number | null
  >(null);

  const [showSupersededAlert, setShowSupersededAlert] = useState(false);
  const [showPdsFailedAlert, setShowPdsFailedAlert] = useState(false);

  // Whether the merge is in progress
  const [isMerging, setIsMerging] = useState(false);

  // Which type of criteria the user is currently using to search
  // Default to NHS number if available, otherwise first available type
  const [criteriaType, setCriteriaType] = useState<ReconciliationCriteriaType>(
    getDefaultCriteriaType,
  );

  // State for org selection when pdsOrg is not provided
  const [selectedOrg, setSelectedOrg] = useState<SelectedTrust>(null);
  const [confirmedOrg, setConfirmedOrg] = useState<{
    odsCode: string;
  } | null>(null);

  // Determine if we need org selection flow
  const needsOrgSelection = pdsOrg === undefined && confirmedOrg === null;

  // The effective pdsOrg to use for searches (either passed in or confirmed by user)
  // When !needsOrgSelection, this is guaranteed to be non-null
  const effectivePdsOrg: { odsCode: string } | null = pdsOrg ?? confirmedOrg;

  // Helper to get the pdsOrg for API calls - throws if called when null
  const getPdsOrgForSearch = () => {
    if (!effectivePdsOrg) {
      throw new Error("Cannot search without a PDS org selected");
    }
    return effectivePdsOrg;
  };

  // Reset the search results when the criteria type changes
  useEffect(() => {
    setSearchResultsAndHandlers(undefined);
    setSearchResultIndexSelected(null);
  }, [criteriaType]);

  // Calculate the overall state of the page
  const state =
    searchResultsAndHandlers === undefined
      ? "search"
      : searchResultsAndHandlers.searchResults === "loading"
        ? "searching"
        : searchResultsAndHandlers.searchResults.searchOutcome ===
            "too-many-matches"
          ? "too-many-matches"
          : searchResultsAndHandlers.searchResults.pdsResult ===
              "too-many-matches"
            ? "too-many-matches-pds"
            : searchResultsAndHandlers.searchResults.matchedPatients.length ===
                0
              ? "results-not-found"
              : searchResultIndexSelected === null
                ? "results-found"
                : "result-selected";

  const onContinueFn =
    (data: SuccessPatientIndexSearchResultV2) => async (index: number) => {
      const selectedPatient = data.matchedPatients[index];
      if (selectedPatient === undefined) return;
      const selectedSearchResultId = selectedPatient.resultId;

      setIsMerging(true);

      try {
        // For reconciliation, we pass pdsOrg and formContextId instead of reason
        const searchReasoning: SaveSearchReasoningV2Request = {
          pdsOrg: getPdsOrgForSearch(),
          ...(formContextId ? { formContextId } : {}),
        };

        // Record the search reason and create patient if needed
        const result = await apiClient.addSearchV2Reasoning(
          selectedSearchResultId,
          searchReasoning,
        );

        if (result.status !== 200) {
          renderErrorToast({
            message:
              "Failed to record search reasoning - please try your search again",
          });
          setIsMerging(false);
          return;
        }

        // Get the patientId from the response - handle all response types
        const responseData = result.data;
        let patientId: string | undefined;

        if (
          responseData.type === "patient-page" ||
          responseData.type === "s-flag-setup"
        ) {
          patientId = responseData.patientId;
        } else {
          // For suggested-merge, use the first patient
          patientId = responseData.patients[0]?.patientId;
        }

        if (!patientId) {
          renderErrorToast({
            message: "Could not determine patient to merge - please try again",
          });
          setIsMerging(false);
          return;
        }

        // Now merge the found patient into the primary patient
        const mergeResult = await apiClient.mergePatients({
          currentPatientId: currentPatientId,
          targetPatientId: patientId, // PDS patient will always be the primary
          reason: "Patient reconciliation",
          isAutoMerge: true,
        });

        if (mergeResult.status === 204) {
          onComplete({ type: "patient-merged", patientId });
        } else {
          renderErrorToast({
            message: `Failed to merge patient - ${mergeResult.data.reason ?? "Unknown error"}`,
          });
        }
      } catch {
        renderErrorToast({
          message: "An error occurred - please try again",
        });
      } finally {
        setIsMerging(false);
      }
    };

  // Search for patients using NHS Number
  const searchByNhsNumber: CollectCriteriaNhsNumberProps["onSubmit"] = async (
    values,
  ) => {
    setSearchResultsAndHandlers({ searchResults: "loading" });

    const { data } = await apiClient.searchV2(v4(), {
      type: "nhs-number",
      nhsNumber: values.nhsNumber,
      patientSearchVariant: "reconciliation",
      pdsOrg: getPdsOrgForSearch(),
      ...(formContextId ? { formContextId } : {}),
    });

    if (data.searchOutcome === "too-many-matches") {
      setSearchResultsAndHandlers({ searchResults: data });
      return;
    }

    if (data.nhsNumberWasSuperseded) {
      setShowSupersededAlert(true);
    }

    if (data.pdsResult === "error") {
      setShowPdsFailedAlert(true);
    }

    setSearchResultsAndHandlers({
      searchResults: data,
      onContinue: onContinueFn(data),
    });
  };

  // Search for patients using local Demographics
  const searchByDemographics: CollectCriteriaDemographicsProps["onSubmit"] =
    async (values) => {
      setSearchedDemographics(values);
      setSearchResultsAndHandlers({ searchResults: "loading" });

      const { data } = await apiClient.searchV2(v4(), {
        type: "demographics",
        enablePhoneticMatchOnSurname: values.usePhoneticSurnameMatch,
        enablePhoneticMatchOnGivenName: values.usePhoneticGivenNameMatch,
        given: values.givenName ?? undefined,
        surname: values.familyName ?? "",
        birthdate: values.dateOfBirthIsExact
          ? values.dateOfBirthExact
          : ageRangeToDateRange(values.ageApprox),
        patientSearchVariant: "reconciliation",
        pdsOrg: getPdsOrgForSearch(),
        ...(formContextId ? { formContextId } : {}),
      });

      if (data.searchOutcome === "too-many-matches") {
        setSearchResultsAndHandlers({ searchResults: data });
        return;
      }

      setSearchResultsAndHandlers({
        searchResults: data,
        onContinue: onContinueFn(data),
      });
    };

  // Search for patients using PDS Demographics
  const searchByPdsDemographics: CollectCriteriaPdsDemographicsProps["onSubmit"] =
    async (values) => {
      setSearchedDemographics({
        familyName: values.familyName,
        givenName: values.givenName,
        dateOfBirthExact: values.dateOfBirthExact,
        dateOfBirthIsExact: values.dateOfBirthIsExact,
        usePhoneticGivenNameMatch: false,
        usePhoneticSurnameMatch: false,
        ageApprox: values.ageApprox,
      });
      setSearchResultsAndHandlers({ searchResults: "loading" });

      const { data } = await apiClient.searchV2(v4(), {
        type: "pds-demographics",
        postcode: values.postcode ?? undefined,
        given: values.givenName ?? undefined,
        surname: values.familyName ?? "",
        birthdate: values.dateOfBirthIsExact
          ? values.dateOfBirthExact
          : ageRangeToDateRange(values.ageApprox),
        patientSearchVariant: "reconciliation",
        pdsOrg: getPdsOrgForSearch(),
        ...(formContextId ? { formContextId } : {}),
      });

      if (data.searchOutcome === "too-many-matches") {
        setSearchResultsAndHandlers({ searchResults: data });
        return;
      }

      if (data.pdsResult === "error") {
        setShowPdsFailedAlert(true);
      }

      setSearchResultsAndHandlers({
        searchResults: data,
        onContinue: onContinueFn(data),
      });
    };

  const tooManyMatches = state === "too-many-matches";
  const tooManyMatchesPds = state === "too-many-matches-pds";

  // Count how many criteria types are available
  const availableCriteriaCount = [
    canSearchByPdsNhsNumber,
    canSearchByDemographics,
    canSearchByPdsDemographics,
  ].filter(Boolean).length;

  return (
    <PopupDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <PopupDialogTitle
        titleText="Patient Reconciliation Search"
        closeDialog={onClose}
        removeMarginBottom
      />

      <Box sx={{ px: 2, pt: 2, pb: 0 }}>
        {currentPatient && (
          <Box sx={{ mb: 2 }} data-testid="reconciliation-patient-banner">
            <PatientBanner
              patient={currentPatient}
              nhsNumber={currentPatientNhsNumber}
              isHidden
              disableSticky
            />
          </Box>
        )}

        {(canSearchByPdsNhsNumber || canSearchByPdsDemographics) && (
          <Banner
            sx={{ mb: 2 }}
            bannerType={BannerList.INFO}
            title="Search for a patient record via PDS (The Spine) to reconcile the current form against"
          />
        )}
        <SupersededPatientPopup
          open={showSupersededAlert}
          setOpen={setShowSupersededAlert}
        />

        <PdsSearchFailedPopup
          open={showPdsFailedAlert}
          setOpen={setShowPdsFailedAlert}
        />

        {/* Org selection UI when pdsOrg is not provided */}
        {needsOrgSelection && (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Please select the trust you are working for
            </Typography>
            <TrustPicker
              value={selectedOrg}
              onChange={setSelectedOrg}
              allowNotListed={false}
              label="Trust"
              testId="reconciliation-trust-picker"
            />
            <FormFooterSection
              customFooterBackgroundColor={theme.palette.common.white}
              saveVariant="contained"
              saveLabel={t("buttonLabels.continue")}
              onSave={() => {
                if (selectedOrg && selectedOrg.id && selectedOrg.odsCode) {
                  setConfirmedOrg({
                    odsCode: selectedOrg.odsCode,
                  });
                }
              }}
              disableSubmit={
                !selectedOrg || !selectedOrg.id || !selectedOrg.odsCode
              }
              performSaveActionOnFormSubmit={false}
              secondaryLeftButton={<></>}
              discardLabel={t("buttonLabels.cancel")}
              onCancel={onClose}
            />
          </Box>
        )}

        {/* Criteria type selection - only show if more than one option available */}
        {!needsOrgSelection && availableCriteriaCount > 1 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Search Method
            </Typography>
            <FormControl>
              <RadioGroup
                value={criteriaType}
                onChange={(event) => {
                  const value = event.target
                    .value as ReconciliationCriteriaType;
                  if (
                    ["nhs-number", "demographics", "pds-demographics"].includes(
                      value,
                    )
                  ) {
                    setCriteriaType(value);
                  }
                }}
              >
                {canSearchByPdsNhsNumber && (
                  <FormControlLabel
                    data-testid="radio-pds-nhs-number"
                    value="nhs-number"
                    control={<Radio />}
                    label="Search by NHS Number"
                    disabled={
                      ["searching", "result-selected"].includes(state) ||
                      isMerging
                    }
                  />
                )}
                {canSearchByPdsDemographics && (
                  <FormControlLabel
                    data-testid="radio-pds-demographics"
                    value="pds-demographics"
                    control={<Radio />}
                    label="Search by PDS Demographics"
                    disabled={
                      ["searching", "result-selected"].includes(state) ||
                      isMerging
                    }
                  />
                )}
                {canSearchByDemographics && (
                  <FormControlLabel
                    data-testid="radio-demographics"
                    value="demographics"
                    control={<Radio />}
                    label="Search by Demographics"
                    disabled={
                      ["searching", "result-selected"].includes(state) ||
                      isMerging
                    }
                  />
                )}
              </RadioGroup>
            </FormControl>
            <HorizontalLine />
          </Box>
        )}

        {!needsOrgSelection && (
          <CollectCriteria
            canUsePhoneticNameMatch={false}
            onSubmitNhsNumber={searchByNhsNumber}
            onSubmitDemographics={searchByDemographics}
            onSubmitPdsDemographics={searchByPdsDemographics}
            criteriaType={criteriaType}
            disabled={
              ["searching", "result-selected"].includes(state) || isMerging
            }
            lastSearchedDemographics={searchedDemographics}
          >
            {({ onClear }) => {
              return (
                <>
                  <SearchResults
                    searchResult={
                      tooManyMatches
                        ? "too-many-matches"
                        : tooManyMatchesPds
                          ? "too-many-matches-pds"
                          : (searchResultsAndHandlers?.searchResults as SuccessPatientIndexSearchResultV2)
                    }
                    onSelectedIndexChanged={setSearchResultIndexSelected}
                    canSearchByPdsDemographics
                    criteriaType={criteriaType}
                    setCriteriaType={setCriteriaType}
                    alternativeInstruction=""
                  />

                  <FormFooterSection
                    customFooterBackgroundColor={theme.palette.common.white}
                    saveVariant="contained"
                    saveLabel={
                      [
                        "search",
                        "searching",
                        "results-not-found",
                        "too-many-matches",
                        "too-many-matches-pds",
                        "results-found",
                      ].includes(state)
                        ? t("buttonLabels.search")
                        : isMerging
                          ? "Merging..."
                          : t("buttonLabels.continue")
                    }
                    onSave={
                      state === "result-selected"
                        ? () => {
                            if (searchResultIndexSelected !== null) {
                              void searchResultsAndHandlers?.onContinue?.(
                                searchResultIndexSelected,
                              );
                            }
                          }
                        : () => {}
                    }
                    disableSubmit={["searching"].includes(state) || isMerging}
                    performSaveActionOnFormSubmit={[
                      "search",
                      "results-not-found",
                      "results-found",
                      "too-many-matches",
                      "too-many-matches-pds",
                    ].includes(state)}
                    // No Create Patient button for reconciliation
                    secondaryLeftButton={<></>}
                    discardLabel={t("buttonLabels.cancel")}
                    disableDiscard={["searching"].includes(state) || isMerging}
                    onCancel={() => {
                      onClear();
                      setSearchedDemographics(undefined);
                      setSearchResultsAndHandlers(undefined);
                      setSearchResultIndexSelected(null);
                      onClose();
                    }}
                  />
                </>
              );
            }}
          </CollectCriteria>
        )}
      </Box>
    </PopupDialog>
  );
};
